import dynamic from "next/dynamic";
import Head from "next/head";
import { useAuth } from "utils/auth";
import { useRouter } from "next/router";
import { GooseArticleQuery, GooseArticleQueryVariables } from "types/api";

import { useTrackPageView } from "utils/analytics";
import { gql, useQuery } from "utils/urql";
import { throwCorrectStatus } from "utils/error";

import ArticleLayout from "components/article/goose/ArticleLayout";
import ArticleJsonLd from "components/article/goose/ArticleJsonLd";
import ArticleMetadata from "components/article/goose/ArticleMetadata";
import ArticleHeader from "components/article/goose/ArticleHeader";
import ArticleLeadArt from "components/article/goose/ArticleLeadArt";
import EditorsNote from "components/article/goose/EditorsNote";
import ArticleContent from "components/article/goose/ArticleContent";
import ArticleBio from "components/article/goose/ArticleBio";
import ArticleFoundationLine from "components/article/goose/ArticleFoundationLine";
import ArticleBentoRecirc from "components/article/goose/ArticleBentoRecirc";

export const GOOSE_ARTICLE_QUERY = gql`
  ${ArticleLeadArt.fragment}
  ${EditorsNote.fragment}
  ${ArticleContent.fragment}
  ${ArticleBio.fragment}
  ${ArticleHeader.fragment}
  ${ArticleMetadata.fragment}
  ${ArticleFoundationLine.fragment}
  ${ArticleJsonLd.fragment}
  ${ArticleBentoRecirc.fragment}

  query GooseArticleQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      __typename
      id
      isTnfCompatible
      datePublished
      title
      slug
      wordCount
      layout
      isFreelance
      isPython3
      preview
      watsonInfo {
        topCategory {
          full
          one
          two
          three
          four
        }
        categories
      }
      grapeshot {
        segments
      }
      editorialMotivation {
        slug
      }
      editorialProject {
        slug
        displayName
        ...articleBentoRecirc
        ...articleFoundationLine
      }
      ...articleMetadata
      ...articleHeader
      ...articleBio
      ...articleJsonLdData

      htmlContentSections {
        ...articleContent
      }
      leadArt {
        ...articleLeadArt
      }
      embeds {
        type
        src
      }
      primaryChannel {
        slug
        displayName
      }
      categories {
        slug
      }
      channels {
        slug
      }
      editorsNote {
        ...editorsNote
      }
      advertisingInfo {
        disableHouseAd
      }
      ... on MagazineArticle {
        issue {
          issueName
          slug
        }
      }
    }
  }
`;

const DynamicPaywall = dynamic(() => import("components/paywall/Paywall"), {
  ssr: false,
});

const DynamicNonMeteredNudge = dynamic(
  () => import("components/NonMeteredNudge"),
  {
    ssr: false,
  }
);

const GooseArticleDisplay = ({ data }: { data: GooseArticleQuery }) => {
  const { hasPaywallAccess } = useAuth();

  useTrackPageView(data);
  return (
    <>
      <Head>
        <title>
          {data.article.seoTitle || data.article.title} - The Atlantic
        </title>
      </Head>
      <ArticleMetadata {...data.article} />
      <ArticleJsonLd {...data.article} />
      <ArticleLayout {...data.article} />

      {/* // import paywall if article is metered; if not, import non-metered nudge */}
      {data.article.hasMeter && !hasPaywallAccess ? (
        <DynamicPaywall />
      ) : (
        <DynamicNonMeteredNudge {...data.article} />
      )}
    </>
  );
};

const GooseArticleFetcher = () => {
  const { query } = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [year, month, slug, id] = query.article as Array<string>;
  const preview = typeof query.preview === "string" ? query.preview : null;

  const [{ error, data }] = useQuery<
    GooseArticleQuery,
    GooseArticleQueryVariables
  >({
    query: GOOSE_ARTICLE_QUERY,
    variables: {
      id,
      preview,
    },
  });

  if (error) {
    throwCorrectStatus({ error });
  }
  return data ? (
    <GooseArticleDisplay key={`article-${data.article.id}`} data={data} />
  ) : null;
};

export default GooseArticleFetcher;
